/**
 * CountdownBadge - Semantic badge for time-based urgency indicators
 *
 * Shows countdown with color based on urgency thresholds.
 *
 * Usage:
 *   <CountdownBadge days={3} />           // 3d in red (urgent)
 *   <CountdownBadge days={10} />          // 10d in yellow (warning)
 *   <CountdownBadge days={21} />          // 21d in gray (normal)
 *   <CountdownBadge days={0} label="Today" />
 */
import { Badge, type BadgeIntent, type BadgeProps } from './Badge'

interface CountdownBadgeProps extends Omit<BadgeProps, 'intent' | 'children'> {
  // Days until event
  days: number
  // Custom label (overrides auto-generated "Xd")
  label?: string
  // Tooltip text
  title?: string
  // Threshold for danger (default: 7 days)
  dangerThreshold?: number
  // Threshold for warning (default: 14 days)
  warningThreshold?: number
  // Show icon
  icon?: React.ReactNode
}

function getCountdownLabel(days: number): string {
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  return `${days}d`
}

export function CountdownBadge({
  days,
  label,
  title,
  dangerThreshold = 7,
  warningThreshold = 14,
  icon,
  variant = 'outline',
  ...badgeProps
}: CountdownBadgeProps) {
  // Determine intent based on urgency
  let intent: BadgeIntent = 'neutral'

  if (days <= dangerThreshold) {
    intent = 'danger'
  } else if (days <= warningThreshold) {
    intent = 'warning'
  }

  const displayLabel = label ?? getCountdownLabel(days)

  return (
    <Badge intent={intent} variant={variant} title={title} {...badgeProps}>
      {icon && <span className="mr-0.5">{icon}</span>}
      {displayLabel}
    </Badge>
  )
}

// Calendar icon for earnings badge convenience
export function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  )
}
